import { useEffect, useState } from "react";
import { useOpenState } from "../../../hooks/useOpenState";
import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { LoadingState } from "../../../components/primitives/feedback/LoadingState";
import { EmptyState } from "../../../components/primitives/feedback/EmptyState";
import { MarkdownWithHighlight } from "../../../components/primitives/data-display/MarkdownWithHighlight";
import {
  FieldReviewPanel,
  FieldReviewTabs,
} from "../../../components/domain/annotation/FieldReviewPanel";
import { getEffectiveScorecard } from "../../../lib/corpusApi";
import { useImproveDocument } from "./useImproveDocument";
import { ROUTES } from "../../../route_paths";

interface AnnotationSplitPaneProps {
  left: ReactNode;
  right: ReactNode;
}

function AnnotationSplitPane({ left, right }: AnnotationSplitPaneProps) {
  return (
    <Stack
      direction={{ xs: "column-reverse", md: "row" }}
      sx={{ flex: { md: 1 }, overflow: { md: "hidden" } }}
    >
      <Stack
        sx={{
          flex: { md: 1 },
          minWidth: 0,
          height: { xs: "50vh", md: "100%" },
          overflow: { md: "hidden" },
          borderTop: { xs: "1px solid", md: "none" },
          borderColor: "divider",
        }}
      >
        {left}
      </Stack>
      <Stack
        sx={{
          flex: { md: "0 0 380px" },
          minWidth: 0,
          overflow: { md: "hidden" },
          borderLeft: { md: "1px solid" },
          borderColor: "divider",
        }}
      >
        {right}
      </Stack>
    </Stack>
  );
}

/**
 * Improve Ranking per-document view (TAN-37, deferred): works through this one document's
 * unextracted fields in potential-point-contribution order (not tier — unlike the primary
 * backlog). No required save; any improvement is a gain, and there is no auto-ingest concern
 * since the document is already ingested and searchable.
 */
export default function ImproveDocument() {
  const { id } = useParams<{ id: string }>();
  const {
    document,
    field,
    loading,
    error,
    saving,
    savedMessage,
    override,
    openOverride,
    approve,
    markEmpty,
  } = useImproveDocument(id ?? "");

  const help = useOpenState();
  const [alert, setAlert] = useState<{
    severity: "success" | "error";
    message: string;
  } | null>(null);

  useEffect(() => {
    if (savedMessage) setAlert({ severity: "success", message: savedMessage });
  }, [savedMessage]);

  useEffect(() => {
    if (error) setAlert({ severity: "error", message: error });
  }, [error]);

  if (loading && !document) {
    return <LoadingState label="Loading document…" />;
  }

  if (error && !document) {
    return <EmptyState title="Something went wrong" message={error} />;
  }

  if (!document) return null;

  if (!field) {
    return (
      <EmptyState
        title="Nothing left to improve"
        message="Every field on this document is already extracted."
        action={
          <Button component={Link} to={ROUTES.internal.annotation.path()} variant="outlined">
            Back to Annotation
          </Button>
        }
      />
    );
  }

  const effectiveScorecard = getEffectiveScorecard(document.ai_scorecard);
  const preAnnotation = effectiveScorecard?.[field] ?? {
    labels: [],
    quote: null,
    confidence: null,
  };

  return (
    <Stack sx={{ height: { md: "100vh" }, overflow: { md: "hidden" } }}>
      <AnnotationSplitPane
        left={
          <MarkdownWithHighlight
            markdownText={document.markdown_text ?? ""}
            quote={preAnnotation.quote}
            title={document.title ?? "(no title)"}
            sourceUrl={document.url}
          />
        }
        right={
          <FieldReviewTabs
            scorecard={effectiveScorecard}
            annotation={document.annotation}
            activeField={field}
            sourceId={id ?? ""}
            review={
              <FieldReviewPanel
                field={field}
                preAnnotation={preAnnotation}
                override={override}
                saving={saving}
                helpOpen={help.open}
                onApprove={approve}
                onOverride={openOverride}
                onMarkEmpty={markEmpty}
                onHelpToggle={help.toggle}
              />
            }
          />
        }
      />

      {alert && (
        <Snackbar
          open
          autoHideDuration={6000}
          onClose={() => setAlert(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ width: "100%" }}>
            {alert.message}
          </Alert>
        </Snackbar>
      )}
    </Stack>
  );
}
